import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, ClipboardCheck, Loader2, Images, MessageSquareText, Mail, BellOff, BellRing, Siren, Send } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_CLS = {
  "VERIFIED": "border-emerald-500/60 text-emerald-300 bg-emerald-500/10",
  "EXPIRING SOON": "border-[#F2B705]/60 text-[#F2B705] bg-[#F2B705]/10",
  "EXPIRED": "border-red-500/60 text-red-300 bg-red-500/10",
  "NEEDS REVIEW": "border-orange-400/60 text-orange-300 bg-orange-400/10",
};
const UPLOAD_BADGE = {
  pending_review: ["UPLOADED — PENDING REVIEW", "border-[#F2B705]/60 text-[#F2B705] bg-[#F2B705]/10"],
  verified: ["UPLOAD VERIFIED", "border-emerald-500/60 text-emerald-300 bg-emerald-500/10"],
  rejected: ["UPLOAD REJECTED", "border-red-500/60 text-red-300 bg-red-500/10"],
};
const inp = "w-full bg-black border border-white/15 rounded px-2 py-1.5 text-white text-xs";
const SECTION_LABELS = {
  renter_info: "Renter Info (name, phone, email, address, DL)",
  booking_details: "Booking / Rental Details",
  accident_details: "Accident / Incident Details",
  license_photos: "Driver's License Photo(s)",
  signed_agreement: "Signed Rental Agreement (PDF)",
  damage_photos: "Accident/Damage Photos & Videos (links)",
  payment_history: "Payment History",
};
const DETECTIVE_DEFAULTS = { renter_info: true, booking_details: true, accident_details: true,
  license_photos: false, signed_agreement: false, damage_photos: false, payment_history: false };

const PF = ({ label, value, onChange, testid }) => (
  <button data-testid={testid} onClick={() => onChange(!value)}
    className={`text-[11px] font-bold px-2.5 py-1.5 rounded border transition-colors ${value ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10" : "border-red-500/60 text-red-300 bg-red-500/10"}`}>
    {label}: {value ? "PASS" : "FAIL"}
  </button>
);

export const ReturnCompliancePanel = ({ booking, token, onSaved }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [ins, setIns] = useState(null);
  const [form, setForm] = useState({ company: "", policy_number: "", named_insured: "", effective_date: "", expiration_date: "", note: "" });
  const [showVerify, setShowVerify] = useState(false);
  const [insp, setInsp] = useState({ interior_clean: true, items_removed: true, fuel_restored: true, new_damage: false, on_time: true, notes: "" });
  const [showInsp, setShowInsp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState("");
  const [pre, setPre] = useState(null);
  const [showPre, setShowPre] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [accidents, setAccidents] = useState([]);
  const [detectiveModal, setDetectiveModal] = useState(null);

  const load = async () => {
    try {
      const [a, c, acc] = await Promise.all([
        axios.get(`${API}/bookings/${booking.id}/insurance`, auth),
        axios.get(`${API}/dnr-reviews?status=pending`, auth),
        axios.get(`${API}/accident-reports?booking_id=${booking.id}`, auth),
      ]);
      setIns(a.data);
      setReviews(c.data.filter((r) => r.booking_id === booking.id));
      setAccidents(acc.data.reports || []);
      const iv = a.data.insurance_verification || {};
      setForm((f) => ({ ...f, company: iv.company || "", policy_number: iv.policy_number || "", named_insured: iv.named_insured || booking.name, effective_date: iv.effective_date || "", expiration_date: iv.expiration_date || "" }));
    } catch { /* noop */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [booking.id]);

  const verify = async () => {
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/insurance-verification`, form, auth);
      toast.success(`Insurance verified — ${data.status}. Next check ${data.insurance_verification.next_verification_date}.`);
      setShowVerify(false); load(); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not verify."); }
  };

  const submitInspection = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/return-inspection`, insp, auth);
      toast.success(`Inspection saved${data.charges_applied ? ` — $${data.charges_applied.toFixed(2)} in charges applied` : ""}${data.dnr_review ? ". ⚠ DNR review recommended." : "."}`);
      setShowInsp(false); load(); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save inspection."); }
    finally { setSaving(false); }
  };

  const sendLink = async (method) => {
    setSending(method);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/insurance-link/send`, { method }, auth);
      const ok = Object.entries(data.results).filter(([, v]) => v).map(([k]) => k.toUpperCase()).join(" + ");
      toast.success(ok ? `Upload link sent via ${ok}.` : "Send attempted — check delivery logs.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send link."); }
    finally { setSending(""); }
  };

  const toggleReminders = async (stop) => {
    const note = stop ? (window.prompt("Reason for stopping reminders (optional):") ?? "") : "";
    try {
      await axios.post(`${API}/bookings/${booking.id}/insurance-reminders/toggle`, { stop, note }, auth);
      toast.success(stop ? "Automatic insurance reminders stopped." : "Automatic insurance reminders resumed.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update reminders."); }
  };

  const decideUpload = async (uid, action) => {
    const note = window.prompt(action === "approve" ? "Approval note (optional):" : "Rejection reason (renter reminders will restart):") ?? null;
    if (note === null) return;
    try {
      await axios.post(`${API}/insurance-uploads/${uid}/decide`, { action, note }, auth);
      toast.success(action === "approve" ? "Insurance approved — status VERIFIED." : "Upload rejected — reminders restarted.");
      load(); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save decision."); }
  };

  const sendAccidentLink = async (method) => {
    setSending(`acc-${method}`);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/accident-link/send`, { method }, auth);
      const ok = Object.entries(data.results).filter(([, v]) => v).map(([k]) => k.toUpperCase()).join(" + ");
      toast.success(ok ? `Accident report link sent via ${ok}.` : "Send attempted — check delivery logs.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send link."); }
    finally { setSending(""); }
  };

  const setAccStatus = async (rid, status) => {
    try {
      await axios.patch(`${API}/accident-reports/${rid}`, { status }, auth);
      toast.success(`Report marked ${status}.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update."); }
  };

  const toggleFatal = async (rid, fatal) => {
    try {
      await axios.post(`${API}/accident-reports/${rid}/fatal`, { fatal }, auth);
      toast.success(fatal ? "Report marked FATAL." : "Fatal flag cleared.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update."); }
  };

  const openDetectiveModal = async (rid) => {
    try {
      const { data } = await axios.get(`${API}/accident-reports/${rid}/detective-bundle`, auth);
      setDetectiveModal({ rid, bundle: data, selected: { ...DETECTIVE_DEFAULTS }, email: data.detective_email || "", step: "select", sending: false });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load case bundle."); }
  };

  const submitDetectiveSend = async () => {
    const dm = detectiveModal;
    setDetectiveModal((m) => ({ ...m, sending: true }));
    try {
      const sections = Object.entries(dm.selected).filter(([, v]) => v).map(([k]) => k);
      await axios.post(`${API}/accident-reports/${dm.rid}/send-to-detective`, { detective_email: dm.email, sections }, auth);
      toast.success("Case sent to detective.");
      setDetectiveModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send.");
      setDetectiveModal((m) => ({ ...m, sending: false }));
    }
  };

  const decide = async (rid, action) => {
    const note = action === "dnr" ? (window.prompt("Reason note for the DNR record:") ?? "") : "";
    if (action === "dnr" && note === null) return;
    try {
      await axios.post(`${API}/dnr-reviews/${rid}/decide`, { action, note }, auth);
      toast.success(action === "dnr" ? "Customer added to Do-Not-Rent." : "Warning cleared.");
      load(); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save decision."); }
  };

  const loadPre = async () => {
    if (showPre) { setShowPre(false); return; }
    try {
      const { data } = await axios.get(`${API}/bookings/${booking.id}/pre-return`, auth);
      setPre(data); setShowPre(true);
    } catch { toast.error("Could not load inspections."); }
  };

  const st = ins?.status || "—";
  return (
    <div className="mt-3 rounded-lg border border-white/10 bg-black/30 p-3 space-y-2.5" data-testid={`compliance-${booking.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-white/40"><ShieldCheck size={13} /> Insurance & Return Compliance</span>
        {ins && ins.byoi && <span data-testid={`ins-status-${booking.id}`} className={`text-[10px] font-black px-2 py-0.5 rounded border ${STATUS_CLS[st] || "border-white/20 text-white/50"}`}>INSURANCE: {st}</span>}
        {ins?.upload_status && UPLOAD_BADGE[ins.upload_status] && (
          <span data-testid={`ins-upload-badge-${booking.id}`} className={`text-[10px] font-black px-2 py-0.5 rounded border ${UPLOAD_BADGE[ins.upload_status][1]}`}>{UPLOAD_BADGE[ins.upload_status][0]}</span>
        )}
        {ins?.reminders?.stopped && <span className="text-[10px] font-bold px-2 py-0.5 rounded border border-white/25 text-white/50">REMINDERS STOPPED</span>}
        {ins && !ins.byoi && <span className="text-[10px] text-white/40">All-In Coverage booking — own-insurance tracking not required</span>}
        {ins?.insurance_verification?.expiration_date && <span className="text-[10px] text-white/45">exp {ins.insurance_verification.expiration_date} · next check {ins.insurance_verification.next_verification_date} · {(ins.history || []).length} prior verification(s)</span>}
      </div>
      {ins?.byoi && (ins.link_last || ins.last_upload_at || ins.reminders?.last_sent_at || ins.insurance_verification?.last_verified_date) && (
        <div data-testid={`ins-timeline-${booking.id}`} className="flex flex-wrap gap-x-4 gap-y-0.5 text-[10px] text-white/45">
          {ins.link_last && <span>Link sent: <b className="text-white/70">{fmtDateTime(ins.link_last.at)}</b> via {ins.link_last.method}{ins.link_last.by && ins.link_last.by !== "system" ? ` by ${ins.link_last.by}` : ins.link_last.by === "system" ? " (auto)" : ""}</span>}
          {ins.reminders?.last_sent_at && <span>Last reminder: <b className="text-white/70">{fmtDateTime(ins.reminders.last_sent_at)}</b> (#{ins.reminders.count || 1})</span>}
          {ins.last_upload_at && <span>Last upload: <b className="text-white/70">{fmtDateTime(ins.last_upload_at)}</b></span>}
          {ins.insurance_verification?.last_verified_date && <span>Last verified: <b className="text-white/70">{ins.insurance_verification.last_verified_date}</b></span>}
          {!ins.reminders?.stopped && ins.reminders?.next_due_at && ins.needs_upload && <span className="text-[#F2B705]/80">Next reminder: <b>{fmtDateTime(ins.reminders.next_due_at)}</b></span>}
        </div>
      )}
      {reviews.map((r) => (
        <div key={r.id} data-testid={`dnr-review-${r.id}`} className="rounded border border-red-500/50 bg-red-500/10 p-2.5">
          <p className="text-red-300 text-xs font-black">⚠ DNR REVIEW RECOMMENDED — {r.reasons?.join(", ")}</p>
          {r.notes && <p className="text-white/60 text-[11px] mt-0.5">{r.notes}</p>}
          <div className="flex gap-2 mt-1.5">
            <button data-testid={`dnr-add-${r.id}`} onClick={() => decide(r.id, "dnr")} className="text-[11px] font-bold border border-red-500/60 text-red-300 px-2.5 py-1 rounded hover:bg-red-500 hover:text-white">Add to DNR</button>
            <button data-testid={`dnr-clear-${r.id}`} onClick={() => decide(r.id, "clear")} className="text-[11px] font-bold border border-emerald-500/50 text-emerald-300 px-2.5 py-1 rounded hover:bg-emerald-400 hover:text-black">Clear Warning</button>
          </div>
        </div>
      ))}
      {(ins?.uploads || []).length > 0 && (
        <div data-testid={`ins-uploads-${booking.id}`} className="rounded border border-[#F2B705]/40 bg-[#F2B705]/[0.06] p-2.5">
          <p className="text-[#F2B705] text-xs font-bold mb-1">🛡 Renter-uploaded insurance — review & verify</p>
          {ins.uploads.map((u) => (
            <div key={u.id} className="text-[11px] text-white/70 flex flex-wrap items-center gap-2 mb-1">
              <span>{fmtDateTime(u.created_at)}{u.company ? ` · ${u.company}` : ""}{u.policy_number ? ` #${u.policy_number}` : ""}{u.expiration_date ? ` · exp ${u.expiration_date}` : ""}</span>
              {(u.doc_urls || []).map((d, i) => <a key={i} href={d} target="_blank" rel="noreferrer" className="underline text-[#F2B705]">doc {i + 1}</a>)}
              {(u.status || "pending") === "pending" ? (
                <>
                  <button data-testid={`ins-upload-approve-${u.id}`} onClick={() => decideUpload(u.id, "approve")}
                    className="text-[10px] font-bold border border-emerald-500/50 text-emerald-300 px-2 py-0.5 rounded hover:bg-emerald-400 hover:text-black">Approve → Verified</button>
                  <button data-testid={`ins-upload-reject-${u.id}`} onClick={() => decideUpload(u.id, "reject")}
                    className="text-[10px] font-bold border border-red-500/50 text-red-300 px-2 py-0.5 rounded hover:bg-red-500 hover:text-white">Reject</button>
                  <button onClick={() => { setForm((f) => ({ ...f, company: u.company || f.company, policy_number: u.policy_number || f.policy_number, expiration_date: u.expiration_date || f.expiration_date })); setShowVerify(true); }}
                    className="text-[10px] font-bold border border-white/25 text-white/60 px-2 py-0.5 rounded hover:border-[#F2B705] hover:text-[#F2B705]">Use & Verify</button>
                </>
              ) : (
                <span className={`text-[10px] font-bold ${u.status === "approved" ? "text-emerald-300" : "text-red-300"}`}>{u.status.toUpperCase()}{u.decided_by ? ` by ${u.decided_by}` : ""}</span>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        <button data-testid={`accident-link-${booking.id}`} onClick={() => sendAccidentLink("both")} disabled={!!sending}
          className="text-[11px] font-semibold border border-red-400/40 text-red-300 px-2.5 py-1 rounded hover:bg-red-500 hover:text-white disabled:opacity-50">
          {sending === "acc-both" ? <Loader2 size={11} className="inline mr-1 animate-spin" /> : <Siren size={11} className="inline mr-1" />}Send Accident Report Link</button>
        {ins?.byoi && <button data-testid={`ins-verify-btn-${booking.id}`} onClick={() => setShowVerify((v) => !v)} className="text-[11px] font-semibold border border-[#F2B705]/50 text-[#F2B705] px-2.5 py-1 rounded hover:bg-[#F2B705] hover:text-black">{ins?.insurance_verification ? "Reverify / Update Insurance" : "Verify Insurance"}</button>}
        {ins?.byoi && (
          <>
            <button data-testid={`ins-link-sms-${booking.id}`} onClick={() => sendLink("sms")} disabled={!!sending}
              className="text-[11px] font-semibold border border-white/20 text-white/70 px-2.5 py-1 rounded hover:border-[#F2B705] hover:text-[#F2B705] disabled:opacity-50">
              {sending === "sms" ? <Loader2 size={11} className="inline mr-1 animate-spin" /> : <MessageSquareText size={11} className="inline mr-1" />}Text Upload Link</button>
            <button data-testid={`ins-link-email-${booking.id}`} onClick={() => sendLink("email")} disabled={!!sending}
              className="text-[11px] font-semibold border border-white/20 text-white/70 px-2.5 py-1 rounded hover:border-[#F2B705] hover:text-[#F2B705] disabled:opacity-50">
              {sending === "email" ? <Loader2 size={11} className="inline mr-1 animate-spin" /> : <Mail size={11} className="inline mr-1" />}Email Upload Link</button>
            {ins?.reminders?.stopped ? (
              <button data-testid={`ins-rem-resume-${booking.id}`} onClick={() => toggleReminders(false)}
                className="text-[11px] font-semibold border border-emerald-500/40 text-emerald-300 px-2.5 py-1 rounded hover:bg-emerald-400 hover:text-black"><BellRing size={11} className="inline mr-1" />Resume Reminders</button>
            ) : (
              <button data-testid={`ins-rem-stop-${booking.id}`} onClick={() => toggleReminders(true)}
                className="text-[11px] font-semibold border border-white/20 text-white/50 px-2.5 py-1 rounded hover:border-red-400 hover:text-red-300"><BellOff size={11} className="inline mr-1" />Stop Reminders</button>
            )}
          </>
        )}
        <button data-testid={`return-insp-btn-${booking.id}`} onClick={() => setShowInsp((v) => !v)} className="text-[11px] font-semibold border border-white/20 text-white/70 px-2.5 py-1 rounded hover:border-[#F2B705] hover:text-[#F2B705]"><ClipboardCheck size={11} className="inline mr-1" />Return Inspection</button>
        <button data-testid={`pre-return-btn-${booking.id}`} onClick={loadPre} className="text-[11px] font-semibold border border-white/20 text-white/70 px-2.5 py-1 rounded hover:border-[#F2B705] hover:text-[#F2B705]"><Images size={11} className="inline mr-1" />Pickup vs Pre-Return Photos</button>
      </div>
      {accidents.length > 0 && (
        <div data-testid={`accident-reports-${booking.id}`} className="rounded border border-red-400/25 bg-red-500/[0.04] p-2.5 space-y-2">
          <p className="text-[10px] font-black uppercase tracking-wide text-red-300 flex items-center gap-1"><Siren size={11} /> Accident / Incident Reports ({accidents.length})</p>
          {accidents.map((r) => (
            <div key={r.id} className="text-[11px] text-white/70 border-b border-white/5 pb-1.5 last:border-0 last:pb-0">
              <div className="flex flex-wrap items-center gap-2">
                <b className="text-white">{r.accident_date}{r.accident_time ? ` ${r.accident_time}` : ""}</b>
                <span>· {r.location}</span>
                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded border ${r.status === "closed" ? "border-white/25 text-white/50" : r.status === "reviewing" ? "border-[#F2B705]/50 text-[#F2B705]" : "border-red-400/50 text-red-300"}`}>{(r.status || "new").toUpperCase()}</span>
                {r.status !== "reviewing" && r.status !== "closed" && <button data-testid={`acc-review-${r.id}`} onClick={() => setAccStatus(r.id, "reviewing")} className="text-[9px] font-bold border border-[#F2B705]/40 text-[#F2B705] px-1.5 py-0.5 rounded hover:bg-[#F2B705] hover:text-black">Mark Reviewing</button>}
                {r.status !== "closed" && <button data-testid={`acc-close-${r.id}`} onClick={() => setAccStatus(r.id, "closed")} className="text-[9px] font-bold border border-white/25 text-white/50 px-1.5 py-0.5 rounded hover:border-white/50">Close</button>}
                <button data-testid={`acc-fatal-${r.id}`} onClick={() => toggleFatal(r.id, !r.fatal)}
                  className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${r.fatal ? "border-red-600 text-red-400 bg-red-600/20" : "border-white/25 text-white/40 hover:border-red-500 hover:text-red-400"}`}>
                  {r.fatal ? "⚠ FATAL" : "Mark Fatal"}
                </button>
                {r.fatal && (
                  <button data-testid={`acc-send-detective-${r.id}`} onClick={() => openDetectiveModal(r.id)}
                    className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-red-500 text-white bg-red-600/80 hover:bg-red-600">
                    <Send size={9} className="inline mr-0.5" />Send to Detective
                  </button>
                )}
              </div>
              <div className="text-white/55 mt-0.5">"{r.statement}"</div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-white/45 mt-0.5">
                {r.injuries && <span>Injuries: {r.injuries}</span>}
                {r.damage_description && <span>Damage: {r.damage_description}</span>}
                {r.other_driver?.name && <span>Other driver: {r.other_driver.name} {r.other_driver.phone} {r.other_driver.insurance}{r.other_driver.policy ? ` #${r.other_driver.policy}` : ""}</span>}
                {r.police_report_number && <span>Police report: {r.police_report_number}</span>}
                {r.witnesses && <span>Witnesses: {r.witnesses}</span>}
                <span>Filed: {r.created_label}</span>
                {(r.media_urls || []).map((m, i) => <a key={i} href={m} target="_blank" rel="noreferrer" className="text-[#F2B705] underline">file {i + 1}</a>)}
              </div>
            </div>
          ))}
        </div>
      )}
      {showVerify && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 rounded border border-white/10 p-2.5">
          {[["company", "Insurance company *"], ["policy_number", "Policy number *"], ["named_insured", "Named insured"]].map(([k, l]) => (
            <label key={k} className="text-[10px] text-white/45">{l}<input data-testid={`ins-${k}-${booking.id}`} className={inp} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} /></label>))}
          <label className="text-[10px] text-white/45">Effective date<input type="date" className={inp} value={form.effective_date} onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))} /></label>
          <label className="text-[10px] text-white/45">Expiration date *<input type="date" data-testid={`ins-exp-${booking.id}`} className={inp} value={form.expiration_date} onChange={(e) => setForm((f) => ({ ...f, expiration_date: e.target.value }))} /></label>
          <label className="text-[10px] text-white/45">Note<input className={inp} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} /></label>
          <button data-testid={`ins-save-${booking.id}`} onClick={verify} className="col-span-2 sm:col-span-3 bg-[#F2B705] text-black text-xs font-bold py-2 rounded hover:bg-white">Save Verification (kept in history)</button>
        </div>
      )}
      {showInsp && (
        <div className="rounded border border-white/10 p-2.5 space-y-2">
          <div className="flex flex-wrap gap-1.5">
            <PF testid={`insp-clean-${booking.id}`} label="Interior Clean" value={insp.interior_clean} onChange={(v) => setInsp((s) => ({ ...s, interior_clean: v }))} />
            <PF testid={`insp-items-${booking.id}`} label="Items Removed" value={insp.items_removed} onChange={(v) => setInsp((s) => ({ ...s, items_removed: v }))} />
            <PF testid={`insp-fuel-${booking.id}`} label="Fuel Restored" value={insp.fuel_restored} onChange={(v) => setInsp((s) => ({ ...s, fuel_restored: v }))} />
            <button data-testid={`insp-damage-${booking.id}`} onClick={() => setInsp((s) => ({ ...s, new_damage: !s.new_damage }))} className={`text-[11px] font-bold px-2.5 py-1.5 rounded border ${insp.new_damage ? "border-red-500/60 text-red-300 bg-red-500/10" : "border-emerald-500/60 text-emerald-300 bg-emerald-500/10"}`}>New Damage: {insp.new_damage ? "YES" : "NO"}</button>
            <button data-testid={`insp-ontime-${booking.id}`} onClick={() => setInsp((s) => ({ ...s, on_time: !s.on_time }))} className={`text-[11px] font-bold px-2.5 py-1.5 rounded border ${insp.on_time ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10" : "border-red-500/60 text-red-300 bg-red-500/10"}`}>Return On Time: {insp.on_time ? "YES" : "NO"}</button>
          </div>
          <input data-testid={`insp-notes-${booking.id}`} className={inp} placeholder="Notes…" value={insp.notes} onChange={(e) => setInsp((s) => ({ ...s, notes: e.target.value }))} />
          <p className="text-white/35 text-[10px]">Failed cleaning/fuel automatically applies the fee from Pricing Settings. Any failure creates a DNR review recommendation (never auto-blacklists).</p>
          <button data-testid={`insp-save-${booking.id}`} onClick={submitInspection} disabled={saving} className="bg-emerald-500 text-black text-xs font-bold px-4 py-2 rounded hover:bg-white disabled:opacity-50">{saving ? "Saving…" : "Save Return Inspection"}</button>
        </div>
      )}
      {showPre && pre && (
        <div className="rounded border border-white/10 p-2.5 space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-white/40">Pickup → Pre-Return → Final Return</p>
          {(pre.pre_return || []).length === 0 && <p className="text-white/45 text-xs">No customer pre-return submission yet — the secure link goes out with the 12-hour return reminder.</p>}
          {(pre.pre_return || []).map((s) => (
            <div key={s.id}>
              <p className="text-white/60 text-[11px] mb-1">Submitted {fmtDateTime(s.created_at)} · mileage {s.mileage || "—"} · fuel {s.fuel_level || "—"} · {s.no_new_damage ? "no new damage reported" : "⚠ damage/issue reported"}{s.comments ? ` · "${s.comments}"` : ""}</p>
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
                {Object.entries(s.photos || {}).map(([k, u]) => u && <a key={k} href={u} target="_blank" rel="noreferrer"><img src={u} alt={k} title={k} className="h-16 w-full object-cover rounded" /></a>)}
                {(s.extra_photos || []).map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer"><img src={u} alt="extra" className="h-16 w-full object-cover rounded border border-red-400/50" /></a>)}
              </div>
            </div>
          ))}
          {pre.pickup_inspection?.photos?.length > 0 && (
            <div>
              <p className="text-white/60 text-[11px] mb-1">Pickup inspection photos (original — never modified)</p>
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
                {pre.pickup_inspection.photos.map((u, i) => <a key={i} href={typeof u === "string" ? u : u.url} target="_blank" rel="noreferrer"><img src={typeof u === "string" ? u : u.url} alt="pickup" className="h-16 w-full object-cover rounded" /></a>)}
              </div>
            </div>
          )}
        </div>
      )}
      {detectiveModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="detective-modal">
          <div className="bg-[#111] border border-red-500/40 rounded-lg max-w-md w-full p-4 space-y-3 max-h-[85vh] overflow-y-auto">
            {detectiveModal.step === "select" ? (
              <>
                <p className="text-red-300 font-black text-sm flex items-center gap-1.5"><Siren size={14} /> Send Case to Detective</p>
                <label className="text-[11px] text-white/50 block">Detective email
                  <input data-testid="detective-email-input" className={inp} value={detectiveModal.email}
                    onChange={(e) => setDetectiveModal((m) => ({ ...m, email: e.target.value }))}
                    placeholder="detective@department.gov" />
                </label>
                <div className="space-y-1.5">
                  {Object.entries(SECTION_LABELS).map(([k, label]) => {
                    const sec = detectiveModal.bundle.sections[k];
                    return (
                      <label key={k} className={`flex items-start gap-2 text-[11px] p-1.5 rounded border cursor-pointer ${sec?.available ? "border-white/10" : "border-white/5 opacity-40 cursor-not-allowed"}`}>
                        <input type="checkbox" data-testid={`detective-section-${k}`} disabled={!sec?.available}
                          checked={!!detectiveModal.selected[k]}
                          onChange={(e) => setDetectiveModal((m) => ({ ...m, selected: { ...m.selected, [k]: e.target.checked } }))} />
                        <span><b className="text-white/85">{label}</b><br /><span className="text-white/40">{sec?.preview || "Not available"}</span></span>
                      </label>
                    );
                  })}
                </div>
                <div className="flex gap-2 justify-end pt-1">
                  <button data-testid="detective-cancel" onClick={() => setDetectiveModal(null)}
                    className="text-[11px] font-bold border border-white/20 text-white/60 px-3 py-1.5 rounded hover:border-white/40">Cancel</button>
                  <button data-testid="detective-review" disabled={!detectiveModal.email.trim() || !Object.values(detectiveModal.selected).some(Boolean)}
                    onClick={() => setDetectiveModal((m) => ({ ...m, step: "confirm" }))}
                    className="text-[11px] font-bold bg-red-600 text-white px-3 py-1.5 rounded disabled:opacity-40 hover:bg-red-500">Review & Send</button>
                </div>
              </>
            ) : (
              <>
                <p className="text-red-300 font-black text-sm flex items-center gap-1.5"><Siren size={14} /> Confirm Send</p>
                <p className="text-white/70 text-[12px]">This will email the following to <b className="text-white">{detectiveModal.email}</b>:</p>
                <ul className="text-[11px] text-white/60 list-disc pl-4 space-y-0.5">
                  {Object.entries(detectiveModal.selected).filter(([, v]) => v).map(([k]) => <li key={k}>{SECTION_LABELS[k]}</li>)}
                </ul>
                <p className="text-red-300/70 text-[10px]">This shares sensitive personal information with a third party. Confirm the recipient is correct before sending.</p>
                <div className="flex gap-2 justify-end pt-1">
                  <button data-testid="detective-back" onClick={() => setDetectiveModal((m) => ({ ...m, step: "select" }))}
                    className="text-[11px] font-bold border border-white/20 text-white/60 px-3 py-1.5 rounded hover:border-white/40">Back</button>
                  <button data-testid="detective-confirm-send" disabled={detectiveModal.sending} onClick={submitDetectiveSend}
                    className="text-[11px] font-bold bg-red-600 text-white px-3 py-1.5 rounded disabled:opacity-50 hover:bg-red-500">
                    {detectiveModal.sending ? "Sending…" : "Confirm & Send"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReturnCompliancePanel;
